import type {
  ShardMigrationJob,
  SyncStatusSummary,
  TenantDeleteResult,
  TenantSyncStatus,
  TenantUser,
  TenantUserInput,
} from '@/types/control/tenant-user';

import { controlApiRequest } from './control-api-client';

/**
 * Tenant users and tenant lifecycle on control-api's admin surface.
 *
 * Three wire quirks are handled here rather than in components.
 *
 * 1. **`created_at` and `last_login` are Unix seconds**, and they differ from
 *    each other: `last_login` is `omitempty` so it is absent for a user who has
 *    never signed in, while `created_at` is not, so a zero time arrives as
 *    `-62135596800` rather than 0.
 * 2. **`DELETE /tenants/{id}` answers `200` for a partial failure** and puts the
 *    truth in the body, so the status code alone reports success for a
 *    half-deleted tenant.
 * 3. **`queue_position` is absent, not null**, while a tenant is running or
 *    ineligible — so it must be read by type, not truthiness.
 */

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function asOptional(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Unix seconds to a Date. Anything at or below zero is "unset": an absent
 * `last_login` arrives as undefined, and a zero `time.Time` marshals as a large
 * negative number rather than 0, so both are rejected by the same test.
 */
function fromUnixSeconds(value: unknown): Date | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return null;
  }
  return new Date(value * 1000);
}

type UserResponse = {
  id?: unknown;
  email?: unknown;
  name?: unknown;
  status?: unknown;
  roles?: unknown;
  created_at?: unknown;
  last_login?: unknown;
};

function toUser(res: UserResponse): TenantUser {
  return {
    id: asString(res.id),
    email: asString(res.email),
    name: asString(res.name),
    status: asString(res.status),
    roles: Array.isArray(res.roles)
      ? (res.roles as unknown[]).map(asString).filter(Boolean)
      : [],
    createdAt: fromUnixSeconds(res.created_at),
    lastLogin: fromUnixSeconds(res.last_login),
  };
}

function toSyncStatus(res: Record<string, unknown>): TenantSyncStatus {
  const trigger = asString(res.running_trigger);

  return {
    tenantId: asString(res.tenant_id),
    tenantName: asString(res.tenant_name) || asString(res.tenant_id),
    lastSyncAt: asOptional(res.last_sync_at),
    isRunning: res.is_running === true,
    runningStartedAt: asOptional(res.running_started_at),
    runningJobId: asOptional(res.running_job_id),
    runningTrigger:
      trigger === 'manual' || trigger === 'automatic' ? trigger : null,
    // Read by type: the key is absent while running or ineligible, and it is
    // 1-based, so a truthiness check would also misread a hypothetical 0.
    queuePosition:
      typeof res.queue_position === 'number' ? res.queue_position : null,
    nextEligibleAt: asOptional(res.next_eligible_at),
    scheduleEnabled: res.schedule_enabled === true,
  };
}

export const tenantUsersService = {
  async list(tenantId: string, signal?: AbortSignal): Promise<TenantUser[]> {
    const res = await controlApiRequest<UserResponse[] | null>({
      path: `/api/admin/tenants/${encodeURIComponent(tenantId)}/users`,
      signal,
    });

    return Array.isArray(res) ? res.map(toUser) : [];
  },

  /**
   * The `201` body is a raw `PlatformUser`, whose `created_at` is RFC3339 rather
   * than the seconds the list endpoint sends — and whose `roles` are absent even
   * when roles were requested, because the handler returns the pre-assignment
   * user. So it is deliberately discarded: the caller refetches instead.
   */
  async add(tenantId: string, input: TenantUserInput): Promise<void> {
    await controlApiRequest<unknown>({
      method: 'POST',
      path: `/api/admin/tenants/${encodeURIComponent(tenantId)}/users`,
      body: { email: input.email, name: input.name },
    });
  },
};

export const tenantAdminService = {
  async syncStatus(signal?: AbortSignal): Promise<SyncStatusSummary> {
    const res = await controlApiRequest<Record<string, unknown> | null>({
      path: '/api/admin/tenants/sync-status',
      signal,
    });

    return {
      tenants: Array.isArray(res?.tenants)
        ? (res.tenants as Record<string, unknown>[]).map(toSyncStatus)
        : [],
      activeCount: asNumber(res?.active_count),
      maxConcurrent: asNumber(res?.max_concurrent),
      queuedCount: asNumber(res?.queued_count),
      asOf: asOptional(res?.as_of),
    };
  },

  async triggerSync(tenantId: string): Promise<void> {
    await controlApiRequest<unknown>({
      method: 'POST',
      path: `/api/admin/tenants/${encodeURIComponent(tenantId)}/sync`,
    });
  },

  /**
   * Enqueues a shard migration. Answers `202`, not `200`: the worker does the
   * work, so the ids are all that comes back and nothing here has finished yet.
   * The migration is idempotent, so a re-run is safe.
   */
  async migrateShards(tenantId: string): Promise<ShardMigrationJob> {
    const res = await controlApiRequest<Record<string, unknown> | null>({
      method: 'POST',
      path: `/api/admin/tenants/${encodeURIComponent(tenantId)}/shard-migrate`,
    });

    return {
      tenantId: asString(res?.tenant_id) || tenantId,
      jobId: asString(res?.job_id),
      taskId: asString(res?.task_id),
    };
  },

  /**
   * Reports a partial failure rather than throwing: the delete did happen, and
   * the caller needs to show what was left behind. Treating a `200` as plain
   * success is how the old page reported a half-deleted tenant as deleted.
   */
  async remove(tenantId: string): Promise<TenantDeleteResult> {
    const res = await controlApiRequest<Record<string, unknown> | null>({
      method: 'DELETE',
      path: `/api/admin/tenants/${encodeURIComponent(tenantId)}`,
    });

    const errors = Array.isArray(res?.errors)
      ? (res.errors as unknown[]).map(asString).filter(Boolean)
      : [];

    return {
      tenantId: asString(res?.tenant_id) || tenantId,
      partial: asString(res?.status) === 'partial' || errors.length > 0,
      errors,
    };
  },
};
